import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, Gauge, Fuel, Cog, Star, ExternalLink, Share2, ClipboardEdit, ClipboardCheck, StickyNote, TrendingUp, Camera, Users, Clock, AlertTriangle, SlidersHorizontal, FileBarChart, Download, Printer, Trash2, ImageOff, DollarSign, History, Swords, Upload, X, ChevronLeft, ChevronRight, Receipt, Paperclip, FileText, List, LayoutGrid, Copy, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import api, { API } from '../lib/api';
import { formatCurrency, formatKm } from '../mock';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useDeepLink } from '../hooks/useDeepLink';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { InspectionReport } from '../components/InspectionReport';
import { uploadPhotoBatch } from '../lib/photo';
import { VideoBlock } from '../components/VideoBlock';
import { VinScanButton } from '../components/VinScanButton';

const statusStyle = {
  'Disponível': 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  'Reservado': 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  'Vendido': 'text-muted-foreground bg-muted/40 border-border',
  'Entregue': 'text-muted-foreground bg-muted/40 border-border',
};

const statusFilters = ['Todos', 'Disponível', 'Reservado', 'Em Recon', 'Vendido', 'Entregue'];
const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const coverUrl = (v) => (v.photos?.length ? `${API}/files/${v.photos[0].path}` : v.image);

const agingCls = (days) => (days >= 90 ? 'text-red-400 bg-red-500/15 border-red-400/30' : days >= 60 ? 'text-orange-400 bg-orange-500/15 border-orange-400/30' : days >= 30 ? 'text-amber-400 bg-amber-500/15 border-amber-400/30' : 'text-muted-foreground bg-muted/60 border-border');

const shareVehicle = async (v) => {
  const trim = v.trim ? ` ${v.trim.split(',')[0]}` : '';
  const link = v.vdp_url || 'https://www.speedmotorsma.com/inventory/';
  const text = `${v.ano} ${v.marca} ${v.modelo}${trim}\n${formatKm(v.km)} · ${formatCurrency(v.preco)}\n${link}`;
  if (navigator.share) {
    try { await navigator.share({ title: `${v.ano} ${v.marca} ${v.modelo}`, text }); return; } catch (e) { if (e?.name === 'AbortError') return; }
  }
  try {
    await navigator.clipboard.writeText(text);
    toast({ title: 'Copiado!', description: 'Informações do veículo copiadas — cole no WhatsApp ou onde quiser.' });
  } catch (e) { toast({ title: 'Não foi possível copiar' }); }
};

/* ---------- Lightbox de fotos ---------- */
const PhotoLightbox = ({ vehicle, onClose }) => {
  const [idx, setIdx] = useState(0);
  const photos = vehicle ? (vehicle.photos?.length ? vehicle.photos.map((p) => `${API}/files/${p.path}`) : (vehicle.image ? [vehicle.image] : [])) : [];

  useEffect(() => { setIdx(0); }, [vehicle?.id]);

  const prev = useCallback(() => setIdx((i) => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    if (!vehicle) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [vehicle, prev, next, onClose]);

  if (!vehicle || photos.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col" data-testid="photo-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-5 py-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <p className="font-display font-bold text-foreground text-lg leading-tight">{vehicle.ano} {vehicle.marca} {vehicle.modelo}</p>
          <p className="text-xs text-foreground/50">#{vehicle.placa} · foto {idx + 1} de {photos.length}</p>
        </div>
        <button data-testid="lightbox-close" onClick={onClose} className="w-10 h-10 rounded-full border border-foreground/20 text-foreground/80 hover:text-foreground hover:border-foreground/50 flex items-center justify-center transition-colors"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center relative px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
        {photos.length > 1 && (
          <button data-testid="lightbox-prev" onClick={prev} className="absolute left-4 w-12 h-12 rounded-full bg-foreground/10 hover:bg-foreground/25 text-foreground flex items-center justify-center transition-colors"><ChevronLeft className="w-6 h-6" /></button>
        )}
        <img src={photos[idx]} alt={`${vehicle.modelo} ${idx + 1}`} data-testid="lightbox-image" className="max-h-full max-w-full object-contain rounded-lg select-none" draggable={false} />
        {photos.length > 1 && (
          <button data-testid="lightbox-next" onClick={next} className="absolute right-4 w-12 h-12 rounded-full bg-foreground/10 hover:bg-foreground/25 text-foreground flex items-center justify-center transition-colors"><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-2 px-5 py-4 overflow-x-auto justify-center" onClick={(e) => e.stopPropagation()}>
          {photos.map((src, i) => (
            <button key={i} data-testid={`lightbox-thumb-${i}`} onClick={() => setIdx(i)} className={`shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 transition-all ${i === idx ? 'border-primary scale-105' : 'border-transparent opacity-50 hover:opacity-90'}`}>
              <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Diálogo de gestão do veículo (abas) ---------- */
const emptyExpense = () => ({ category: 'Mecânica', description: '', amount: '', date: new Date().toISOString().slice(0, 10), vendor: '', notes: '' });

const VEHICLE_COLORS = ['Preto', 'Branco', 'Prata', 'Cinza', 'Azul', 'Azul Escuro', 'Vermelho', 'Vinho', 'Verde', 'Bege', 'Marrom', 'Dourado', 'Amarelo', 'Laranja', 'Grafite'];

const VehicleManageDialog = ({ vehicle, onClose, onSaved, isAdmin }) => {
  const [tab, setTab] = useState('valores');
  const [fin, setFin] = useState({});
  const [specs, setSpecs] = useState({ marca: '', modelo: '', trim: '', ano: '', km: '', cor: '', placa: '', vin: '', combustivel: 'Flex', cambio: 'Automático', status: 'Disponível' });
  const [savingSpecs, setSavingSpecs] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [comp, setComp] = useState({ name: '', price: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upProgress, setUpProgress] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const photoRef = useRef(null);
  const [exp, setExp] = useState({ expenses: [], total: 0, categories: [], can_manage: false });
  const [expForm, setExpForm] = useState(emptyExpense());
  const [expFiles, setExpFiles] = useState([]);
  const [expSaving, setExpSaving] = useState(false);
  const [upExpId, setUpExpId] = useState(null);
  const newFilesRef = useRef(null);
  const receiptRef = useRef(null);
  const targetExpRef = useRef(null);

  const loadExpenses = useCallback(async () => {
    if (!vehicle) return;
    try { const { data } = await api.get(`/vehicles/${vehicle.id}/expenses`); setExp(data); } catch (e) {}
  }, [vehicle?.id]); // eslint-disable-line

  const loadNotes = useCallback(async () => {
    if (!vehicle) return;
    try { const { data } = await api.get(`/vehicles/${vehicle.id}/notes`); setNotes(data); } catch (e) {}
  }, [vehicle?.id]); // eslint-disable-line

  useEffect(() => {
    if (vehicle?.id) {
      setTab('valores');
      setNoteText('');
      setComp({ name: '', price: '', url: '' });
      setExpForm(emptyExpense());
      setExpFiles([]);
      loadNotes();
      loadExpenses();
    }
  }, [vehicle?.id]); // eslint-disable-line
  useEffect(() => {
    if (vehicle) setFin({ preco: vehicle.preco ?? '', custo: vehicle.custo ?? '', mmr: vehicle.mmr ?? '', title_absent: !!vehicle.title_absent, floor_plan: vehicle.floor_plan ?? '' });
    if (vehicle) setSpecs({
      marca: vehicle.marca ?? '', modelo: vehicle.modelo ?? '', trim: vehicle.trim ?? '', ano: vehicle.ano ?? '',
      km: vehicle.km ?? '', cor: vehicle.cor === '-' ? '' : (vehicle.cor ?? ''), placa: vehicle.placa ?? '',
      vin: vehicle.vin ?? '', combustivel: vehicle.combustivel || 'Flex', cambio: vehicle.cambio || 'Automático',
      status: vehicle.status || 'Disponível',
    });
  }, [vehicle]);

  const saveSpecs = async () => {
    if (!specs.marca.trim() || !specs.modelo.trim()) { toast({ title: 'Informe marca e modelo' }); return; }
    setSavingSpecs(true);
    try {
      const { data } = await api.put(`/vehicles/${vehicle.id}/specs`, specs);
      toast({ title: data.updated ? 'Dados atualizados' : 'Nenhuma alteração' });
      if (data.updated) { onSaved(); loadNotes(); }
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSavingSpecs(false); }
  };

  if (!vehicle) return null;
  const v = vehicle;
  const expTotal = exp.total || 0;
  const margem = (Number(fin.preco) || 0) - (Number(fin.custo) || 0) - expTotal;

  const uploadReceipt = async (eid, files) => {
    if (!files?.length) return;
    setUpExpId(eid);
    let ok = 0;
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/vehicles/${v.id}/expenses/${eid}/receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok += 1;
      } catch (err) { toast({ title: `Erro em ${f.name}`, description: err.response?.data?.detail || '' }); }
    }
    setUpExpId(null);
    if (ok) toast({ title: `${ok} comprovante(s) anexado(s)` });
    await loadExpenses();
  };

  const addExpense = async () => {
    if (!expForm.description.trim() || !(Number(expForm.amount) > 0)) {
      toast({ title: 'Dados obrigatórios', description: 'Informe a descrição e um valor maior que zero.' });
      return;
    }
    setExpSaving(true);
    try {
      const { data } = await api.post(`/vehicles/${v.id}/expenses`, { ...expForm, amount: Number(expForm.amount) });
      if (expFiles.length) await uploadReceipt(data.id, expFiles);
      toast({ title: 'Gasto lançado', description: `${data.category} · ${formatCurrency(data.amount)}` });
      setExpForm(emptyExpense());
      setExpFiles([]);
      await loadExpenses();
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Erro ao lançar gasto', description: e.response?.data?.detail || '' }); }
    finally { setExpSaving(false); }
  };

  const delExpense = async (e2) => {
    if (!window.confirm(`Excluir o gasto "${e2.description}" (${formatCurrency(e2.amount)})?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}/expenses/${e2.id}`);
      toast({ title: 'Gasto excluído' });
      await loadExpenses();
      onSaved && onSaved();
    } catch (err) { toast({ title: 'Erro', description: err.response?.data?.detail || '' }); }
  };

  const delReceipt = async (eid, fid) => {
    try { await api.delete(`/vehicles/${v.id}/expenses/${eid}/receipt/${fid}`); await loadExpenses(); }
    catch (err) { toast({ title: 'Erro', description: err.response?.data?.detail || '' }); }
  };

  const saveFin = async () => {
    setSaving(true);
    try {
      const payload = { preco: Number(fin.preco) || 0 };
      if (isAdmin) { payload.custo = Number(fin.custo) || 0; payload.mmr = Number(fin.mmr) || 0; }
      payload.title_absent = !!fin.title_absent;
      payload.floor_plan = fin.floor_plan || '';
      const { data } = await api.put(`/vehicles/${v.id}/financials`, payload);
      toast({ title: 'Valores atualizados', description: data.changes?.length ? data.changes.join(' · ') : 'Sem alterações.' });
      await loadNotes();
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    const note = noteText.trim();
    if (!note) return;
    try { await api.post(`/vehicles/${v.id}/notes`, { note }); setNoteText(''); await loadNotes(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    const room = 20 - (v.photos?.length || 0);
    const toUp = Array.from(files).slice(0, room);
    if (files.length > room) toast({ title: `Limite de 20 fotos`, description: `Somente ${room} foto(s) serão enviadas.` });
    setUploading(true);
    setUpProgress({ done: 0, total: toUp.length, ok: 0 });
    const { ok, failed } = await uploadPhotoBatch(api, `/vehicles/${v.id}/photos`, toUp, setUpProgress);
    setUploading(false);
    setUpProgress(null);
    if (ok) toast({ title: `${ok} foto(s) adicionada(s)` });
    if (failed.length) toast({ title: `${failed.length} foto(s) não subiram`, description: failed.slice(0, 3).map((f) => `${f.name}: ${f.reason}`).join(' · ') });
    onSaved && onSaved();
  };

  const setCover = async (pid) => {
    try { await api.put(`/vehicles/${v.id}/photos/${pid}/cover`); toast({ title: 'Foto de capa atualizada' }); onSaved && onSaved(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const reorderPhotos = async (from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [...(v.photos || [])];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    try {
      await api.put(`/vehicles/${v.id}/photos/order`, { order: arr.map((p) => p.id) });
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Erro ao reordenar', description: e.response?.data?.detail || '' }); }
  };
  const delPhoto = async (pid) => {
    try { await api.delete(`/vehicles/${v.id}/photos/${pid}`); onSaved && onSaved(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const addCompetitor = async () => {
    if (!comp.name.trim()) { toast({ title: 'Informe o concorrente' }); return; }
    try {
      await api.post(`/vehicles/${v.id}/competitors`, { name: comp.name, price: Number(comp.price) || 0, url: comp.url });
      setComp({ name: '', price: '', url: '' });
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const delCompetitor = async (cid) => {
    try { await api.delete(`/vehicles/${v.id}/competitors/${cid}`); onSaved && onSaved(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const tabs = [['dados', ClipboardEdit, 'Dados'], ['valores', TrendingUp, 'Valores'], ['gastos', Receipt, `Gastos${expTotal ? ` (${formatCurrency(expTotal)})` : ''}`], ['fotos', Camera, `Fotos (${v.photos?.length || 0})`], ['inspecao', ClipboardCheck, 'Inspeção'], ['concorrentes', Swords, 'Concorrentes'], ['historico', History, 'Histórico'], ['anotacoes', StickyNote, 'Anotações']];
  const hist = [...(v.price_history || [])].reverse();

  return (
    <Dialog open={!!vehicle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl uppercase flex flex-wrap items-center gap-2.5" data-testid="vehicle-manage-title">
            {v.ano} {v.marca} {v.modelo} <span className="text-muted-foreground font-semibold text-sm">#{v.placa}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${agingCls(v.aging_days)}`}><Clock className="w-3 h-3 inline mr-1 -mt-0.5" />{v.aging_days}d no estoque</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 flex-wrap bg-muted/40 border border-border rounded-lg p-1 w-fit">
          {tabs.map(([k, Icon, l]) => (
            <button key={k} data-testid={`vm-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === k ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
          ))}
        </div>

        {tab === 'dados' && (
          <div className="rounded-lg border border-border/70 bg-popover p-4 space-y-3" data-testid="vm-specs">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Ano</label><input data-testid="spec-ano" type="number" className={inputCls} value={specs.ano} onChange={(e) => setSpecs({ ...specs, ano: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Marca</label><input data-testid="spec-marca" className={inputCls} value={specs.marca} onChange={(e) => setSpecs({ ...specs, marca: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Modelo</label><input data-testid="spec-modelo" className={inputCls} value={specs.modelo} onChange={(e) => setSpecs({ ...specs, modelo: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Trim</label><input data-testid="spec-trim" className={inputCls} value={specs.trim} onChange={(e) => setSpecs({ ...specs, trim: e.target.value })} /></div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Miles</label>
                <input data-testid="spec-km" type="number" className={inputCls} value={specs.km} onChange={(e) => setSpecs({ ...specs, km: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Cor</label>
                <input data-testid="spec-cor" list="vehicle-colors" className={inputCls} value={specs.cor} onChange={(e) => setSpecs({ ...specs, cor: e.target.value })} placeholder="Prata" />
                <datalist id="vehicle-colors">{VEHICLE_COLORS.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Stock / Placa</label><input data-testid="spec-placa" className={inputCls} value={specs.placa} onChange={(e) => setSpecs({ ...specs, placa: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">VIN</label><div className="flex gap-2"><input data-testid="spec-vin" className={`${inputCls} font-mono flex-1`} value={specs.vin} onChange={(e) => setSpecs({ ...specs, vin: e.target.value.toUpperCase() })} /><VinScanButton testid="spec-vin-scan" onResult={(r) => { const d = r.decoded || {}; setSpecs((s) => ({ ...s, vin: r.vin, marca: d.marca || s.marca, modelo: d.modelo || s.modelo, trim: d.trim || s.trim, ano: d.ano || s.ano })); }} /></div></div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Combustível</label>
                <select data-testid="spec-combustivel" className={inputCls} value={specs.combustivel} onChange={(e) => setSpecs({ ...specs, combustivel: e.target.value })}>
                  {['Gasolina', 'Flex', 'Diesel', 'Híbrido', 'Elétrico'].map((f) => <option key={f} value={f} className="bg-popover">{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Câmbio</label>
                <select data-testid="spec-cambio" className={inputCls} value={specs.cambio} onChange={(e) => setSpecs({ ...specs, cambio: e.target.value })}>
                  {['Automático', 'Manual', 'CVT'].map((c) => <option key={c} value={c} className="bg-popover">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Status</label>
                <select data-testid="spec-status" className={inputCls} value={specs.status} onChange={(e) => setSpecs({ ...specs, status: e.target.value })}>
                  {['Disponível', 'Reservado', 'Em Recon', 'Vendido', 'Entregue'].map((s) => <option key={s} value={s} className="bg-popover">{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border/60">
              <p className="text-[11px] text-muted-foreground/70">Alterações ficam registradas nas anotações do veículo.</p>
              <button data-testid="spec-save" onClick={saveSpecs} disabled={savingSpecs} className="px-4 py-2 rounded-lg text-xs font-bold bg-primary hover:bg-primary/90 disabled:opacity-40 transition-colors">{savingSpecs ? 'Salvando...' : 'Salvar dados'}</button>
            </div>
          </div>
        )}

        {tab === 'valores' && (
          <div className="rounded-lg border border-border/70 bg-popover p-4 space-y-3">
            <div className={`grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Venda ($)</label><input data-testid="fin-preco" type="number" className={inputCls} value={fin.preco} onChange={(e) => setFin({ ...fin, preco: e.target.value })} /></div>
              {isAdmin && <div><label className="text-xs text-muted-foreground mb-1.5 block">Custo ($)</label><input data-testid="fin-custo" type="number" className={inputCls} value={fin.custo} onChange={(e) => setFin({ ...fin, custo: e.target.value })} /></div>}
              {isAdmin && <div><label className="text-xs text-muted-foreground mb-1.5 block">MMR ($)</label><input data-testid="fin-mmr" type="number" className={inputCls} value={fin.mmr} onChange={(e) => setFin({ ...fin, mmr: e.target.value })} /></div>}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border/60">
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                <input data-testid="fin-title-absent" type="checkbox" checked={!!fin.title_absent} onChange={(e) => setFin({ ...fin, title_absent: e.target.checked })} className="accent-blue-500" />
                Title absent (título não está em mãos)
              </label>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Floor plan</label><input data-testid="fin-floor-plan" className={inputCls} value={fin.floor_plan || ''} onChange={(e) => setFin({ ...fin, floor_plan: e.target.value })} placeholder="Ex: NextGear, Westlake, Cash..." /></div>
            </div>
            <div className="flex items-center justify-between">
              {isAdmin ? (
                <p className="text-xs text-muted-foreground">Lucro estimado: <span className={`font-bold ${margem >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="fin-margem">{formatCurrency(margem)}</span>{expTotal > 0 && <span className="text-muted-foreground/70"> (já descontando {formatCurrency(expTotal)} de gastos)</span>}</p>
              ) : <span />}
              <button data-testid="fin-save" onClick={saveFin} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Salvar valores'}</button>
            </div>
            <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {v.leads_count || 0} lead(s) interessados neste veículo</p>
          </div>
        )}

        {tab === 'gastos' && (
          <div className="space-y-3" data-testid="vehicle-expenses-panel">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/70 bg-popover px-4 py-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground/70">Total de gastos</p>
                <p className="font-display font-black text-2xl text-orange-400 tabular-nums" data-testid="expenses-total">{formatCurrency(expTotal)}</p>
              </div>
              <p className="text-[11px] text-muted-foreground text-right">{exp.expenses.length} lançamento(s)<br />{isAdmin ? <>Lucro est.: <b className={margem >= 0 ? 'text-emerald-400' : 'text-red-400'}>{formatCurrency(margem)}</b></> : null}</p>
            </div>

            {exp.can_manage ? (
              <div className="rounded-lg border border-border/70 bg-muted/15 p-3.5 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs text-muted-foreground mb-1.5 block">Categoria</label>
                    <select data-testid="exp-category" className={inputCls} value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })}>
                      {(exp.categories || []).map((c) => <option key={c} className="bg-popover">{c}</option>)}
                    </select>
                  </div>
                  <div><label className="text-xs text-muted-foreground mb-1.5 block">Valor ($) *</label><input data-testid="exp-amount" type="number" step="0.01" className={inputCls} value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} placeholder="450.00" /></div>
                  <div><label className="text-xs text-muted-foreground mb-1.5 block">Data</label><input data-testid="exp-date" type="date" className={inputCls} value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} /></div>
                  <div><label className="text-xs text-muted-foreground mb-1.5 block">Fornecedor</label><input data-testid="exp-vendor" className={inputCls} value={expForm.vendor} onChange={(e) => setExpForm({ ...expForm, vendor: e.target.value })} placeholder="Oficina do João" /></div>
                  <div className="col-span-2 sm:col-span-4"><label className="text-xs text-muted-foreground mb-1.5 block">Descrição *</label><input data-testid="exp-description" className={inputCls} value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} placeholder="Troca de pastilhas e discos de freio" /></div>
                </div>
                <input ref={newFilesRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(e) => { setExpFiles(Array.from(e.target.files)); e.target.value = ''; }} />
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid="exp-pick-files" onClick={() => newFilesRef.current?.click()} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-border text-foreground/75 hover:text-foreground hover:border-primary/50 transition-colors"><Paperclip className="w-3.5 h-3.5" /> Anexar comprovantes</button>
                  {expFiles.length > 0 && <span className="text-[11px] text-muted-foreground" data-testid="exp-files-count">{expFiles.length} arquivo(s): {expFiles.map((f) => f.name).join(', ').slice(0, 60)}</span>}
                  <button data-testid="exp-save" onClick={addExpense} disabled={expSaving} className="ml-auto px-4 py-2 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{expSaving ? 'Lançando...' : 'Lançar gasto'}</button>
                </div>
                <p className="text-[10px] text-muted-foreground/70">Comprovantes: PDF ou imagem (máx 15MB cada). Gastos podem ser lançados também em veículos já vendidos.</p>
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">Somente Administrator, Owner e Managers podem lançar gastos.</p>
            )}

            <input ref={receiptRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(e) => { uploadReceipt(targetExpRef.current, e.target.files); e.target.value = ''; }} />
            <div className="space-y-2" data-testid="expenses-list">
              {exp.expenses.map((e2) => (
                <div key={e2.id} data-testid={`expense-${e2.id}`} className="rounded-lg border border-border/70 px-3.5 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-400/10 text-orange-400 border border-orange-400/25">{e2.category}</span>
                        <p className="text-sm font-semibold truncate">{e2.description}</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">{e2.date}{e2.vendor ? ` · ${e2.vendor}` : ''} · lançado por {e2.created_by}</p>
                    </div>
                    <p className="text-sm font-bold tabular-nums text-orange-400">{formatCurrency(e2.amount)}</p>
                    {exp.can_manage && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid={`exp-attach-${e2.id}`} title="Anexar comprovante" onClick={() => { targetExpRef.current = e2.id; receiptRef.current?.click(); }} disabled={upExpId === e2.id} className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"><Paperclip className="w-4 h-4" /></button>
                        <button data-testid={`exp-del-${e2.id}`} title="Excluir gasto" onClick={() => delExpense(e2)} className="text-red-400/70 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                  {(e2.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {e2.attachments.map((f) => (
                        <span key={f.id} className="inline-flex items-center gap-1.5 text-[11px] rounded-md border border-border/70 bg-muted/25 px-2 py-1">
                          <FileText className="w-3 h-3 text-primary" />
                          <a href={`${API}/files/${f.path}`} target="_blank" rel="noreferrer" data-testid={`exp-receipt-${f.id}`} className="hover:text-primary hover:underline max-w-[160px] truncate">{f.original_filename || 'comprovante'}</a>
                          {exp.can_manage && <button data-testid={`exp-receipt-del-${f.id}`} onClick={() => delReceipt(e2.id, f.id)} className="text-red-400/70 hover:text-red-400"><X className="w-3 h-3" /></button>}
                        </span>
                      ))}
                    </div>
                  )}
                  {upExpId === e2.id && <p className="text-[11px] text-primary mt-2">Enviando comprovante...</p>}
                </div>
              ))}
              {exp.expenses.length === 0 && <p className="text-sm text-muted-foreground/60 text-center py-8">Nenhum gasto lançado neste veículo.</p>}
            </div>
          </div>
        )}

        {tab === 'fotos' && (
          <div className="space-y-3">
            <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ''; }} />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">A <b className="text-foreground">1ª foto é a capa</b> do card. <b className="text-foreground">Arraste as fotos</b> para reordenar. Máx 20.</p>
              <button data-testid="vehicle-photo-add" onClick={() => photoRef.current?.click()} disabled={uploading || (v.photos?.length || 0) >= 20} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"><Camera className="w-4 h-4" /> {uploading ? (upProgress ? `Enviando ${upProgress.done}/${upProgress.total}...` : 'Enviando...') : 'Adicionar fotos'}</button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="vehicle-photos-grid">
              {(v.photos || []).map((p, i) => (
                <div
                  key={p.id}
                  data-testid={`photo-item-${i}`}
                  draggable
                  onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIdx !== i) setOverIdx(i); }}
                  onDragLeave={() => { if (overIdx === i) setOverIdx(null); }}
                  onDrop={(e) => { e.preventDefault(); reorderPhotos(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className={`group/ph relative rounded-lg overflow-hidden border aspect-square bg-black cursor-grab active:cursor-grabbing transition-all ${overIdx === i && dragIdx !== null && dragIdx !== i ? 'border-primary ring-2 ring-primary/60 scale-[1.03]' : 'border-border'} ${dragIdx === i ? 'opacity-40' : ''}`}
                >
                  <img src={`${API}/files/${p.path}`} alt="" className="w-full h-full object-cover pointer-events-none" />
                  {i === 0 && <span className="absolute top-1.5 left-1.5 text-[9px] font-black uppercase tracking-wide bg-primary text-white px-1.5 py-0.5 rounded">Capa</span>}
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-bold bg-black/60 text-foreground/80 px-1.5 py-0.5 rounded border border-border/60">{i + 1}</span>
                  <div className="absolute inset-x-0 bottom-0 flex justify-between items-center gap-1 p-1.5 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover/ph:opacity-100 transition-opacity">
                    {i !== 0 ? <button data-testid={`photo-cover-${p.id}`} onClick={() => setCover(p.id)} className="text-[9px] font-bold text-white bg-primary/80 hover:bg-primary px-1.5 py-1 rounded">Tornar capa</button> : <span />}
                    <button data-testid={`photo-del-${p.id}`} onClick={() => delPhoto(p.id)} className="text-red-400 hover:text-red-300 bg-black/60 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {(v.photos || []).length === 0 && <p className="col-span-full text-sm text-muted-foreground/60 text-center py-8">Nenhuma foto enviada ainda.{v.image ? ' (usando imagem do site como capa)' : ''}</p>}
            </div>
            <div className="border-t border-border pt-3">
              <VideoBlock targetType="vehicle" targetId={v.id} videos={v.videos || []} onChanged={onSaved} />
            </div>
          </div>
        )}

        {tab === 'inspecao' && (
          <div className="rounded-lg border border-border/70 bg-popover p-4" data-testid="vm-inspecao">
            <InspectionReport vehicleId={v.id} />
          </div>
        )}

        {tab === 'concorrentes' && (
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_110px_1fr_auto] gap-2 items-end">
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Concorrente</label><input data-testid="comp-name" className={inputCls} placeholder="Ex: AutoNation Boston" value={comp.name} onChange={(e) => setComp({ ...comp, name: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Preço ($)</label><input data-testid="comp-price" type="number" className={inputCls} value={comp.price} onChange={(e) => setComp({ ...comp, price: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Link (opcional)</label><input className={inputCls} placeholder="https://" value={comp.url} onChange={(e) => setComp({ ...comp, url: e.target.value })} /></div>
              <button data-testid="comp-add" onClick={addCompetitor} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors">Adicionar</button>
            </div>
            <div className="space-y-2" data-testid="comp-list">
              {(v.competitors || []).map((c) => {
                const delta = (v.preco || 0) - (c.price || 0);
                return (
                  <div key={c.id} className="flex items-center gap-3 rounded-lg border border-border/70 px-3.5 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="hover:text-primary hover:underline">{c.name}</a> : c.name}</p>
                      <p className="text-[10px] text-muted-foreground/70">{c.added_by} · {fmtDT(c.added_at)}</p>
                    </div>
                    <p className="text-sm font-bold tabular-nums">{formatCurrency(c.price)}</p>
                    {c.price > 0 && <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${delta <= 0 ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>{delta <= 0 ? `Nosso ${formatCurrency(Math.abs(delta))} mais barato` : `Nosso ${formatCurrency(delta)} mais caro`}</span>}
                    <button data-testid={`comp-del-${c.id}`} onClick={() => delCompetitor(c.id)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
              {(v.competitors || []).length === 0 && <p className="text-sm text-muted-foreground/60 text-center py-6">Nenhum concorrente cadastrado. Nosso preço atual: <b className="text-foreground">{formatCurrency(v.preco)}</b></p>}
            </div>
          </div>
        )}

        {tab === 'historico' && (
          <div className="space-y-2 max-h-[380px] overflow-y-auto" data-testid="price-history">
            {hist.map((h, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-border/70 px-3.5 py-2.5">
                <DollarSign className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">{h.old_price ? <><span className="text-muted-foreground line-through mr-2">{formatCurrency(h.old_price)}</span>{formatCurrency(h.price)}</> : formatCurrency(h.price)}
                    {h.old_price > 0 && <span className={`ml-2 text-[11px] font-bold ${h.price < h.old_price ? 'text-red-400' : 'text-emerald-400'}`}>{h.price < h.old_price ? '▼' : '▲'} {formatCurrency(Math.abs(h.price - h.old_price))}</span>}
                  </p>
                  <p className="text-[10px] text-muted-foreground/70">{h.by} · {h.source === 'sync' ? 'Sincronização do site' : h.source === 'cadastro' ? 'Cadastro' : 'Manual'} · {fmtDT(h.at)}</p>
                </div>
              </div>
            ))}
            {hist.length === 0 && <p className="text-sm text-muted-foreground/60 text-center py-8">Sem histórico de preços ainda — alterações de preço aparecerão aqui.</p>}
          </div>
        )}

        {tab === 'anotacoes' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input data-testid="vehicle-note-input" className={inputCls} placeholder="Escrever anotação..." value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNote()} />
              <button data-testid="vehicle-note-save" onClick={addNote} className="px-4 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 whitespace-nowrap transition-colors">Adicionar</button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="vehicle-notes-feed">
              {notes.map((n) => (
                <div key={n.id} className={`rounded-lg border p-3 ${n.kind === 'financial' ? 'border-primary/25 bg-primary/[0.05]' : 'border-border/70'}`}>
                  <p className="text-[11px] font-bold text-primary">{n.user_name} <span className="text-muted-foreground/60 font-normal">· {n.user_role} · {fmtDT(n.timestamp)}</span>{n.kind === 'financial' && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-primary/80 bg-primary/10 px-1.5 py-0.5 rounded">valores</span>}</p>
                  <p className="text-sm mt-1">{n.note}</p>
                </div>
              ))}
              {notes.length === 0 && <p className="text-sm text-muted-foreground/60 text-center py-4">Nenhuma anotação ainda.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Relatório de Estoque ---------- */
const ReportDialog = ({ open, onClose, isAdmin }) => {
  const [rep, setRep] = useState(null);
  useEffect(() => { if (open) api.get('/vehicles/report').then((r) => setRep(r.data)).catch(() => {}); }, [open]);

  const dlCsv = async () => {
    try {
      const res = await api.get('/vehicles/report/csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'relatorio_estoque.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Erro ao exportar CSV' }); }
  };

  const printReport = () => {
    if (!rep) return;
    const rows = rep.by_marca.map((m) => `<tr><td>${m.marca}</td><td>${m.count}</td><td>$${m.valor.toLocaleString('en-US')}</td></tr>`).join('');
    const admin = isAdmin ? `<div class="kpi"><b>Custo total</b>$${(rep.custo_total || 0).toLocaleString('en-US')}</div><div class="kpi"><b>Gastos total</b>$${(rep.gastos_total || 0).toLocaleString('en-US')}</div><div class="kpi"><b>Lucro estimado</b>$${(rep.lucro_estimado || 0).toLocaleString('en-US')}</div>` : '';
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Relatório de Estoque — Speed Motors</title><style>
      body{font-family:Arial,sans-serif;color:#111;padding:32px}h1{font-size:20px}h2{font-size:15px;margin-top:24px}
      .kpis{display:flex;flex-wrap:wrap;gap:12px;margin-top:16px}.kpi{border:1px solid #ccc;border-radius:8px;padding:10px 16px;font-size:13px}
      .kpi b{display:block;font-size:10px;text-transform:uppercase;color:#666;margin-bottom:4px}
      table{border-collapse:collapse;width:100%;margin-top:8px;font-size:13px}td,th{border:1px solid #ccc;padding:6px 10px;text-align:left}
    </style></head><body>
      <h1>Relatório de Estoque — Speed Motors</h1>
      <p style="font-size:12px;color:#666">Gerado em ${new Date().toLocaleString('en-US')}</p>
      <div class="kpis">
        <div class="kpi"><b>Total de veículos</b>${rep.total}</div>
        <div class="kpi"><b>Disponíveis</b>${rep.disponiveis}</div>
        <div class="kpi"><b>Valor do estoque</b>$${rep.valor_estoque.toLocaleString('en-US')}</div>
        <div class="kpi"><b>Aging médio</b>${rep.aging_medio} dias</div>
        ${admin}
        <div class="kpi"><b>30–59 dias</b>${rep.aging_30}</div>
        <div class="kpi"><b>60–89 dias</b>${rep.aging_60}</div>
        <div class="kpi"><b>90+ dias</b>${rep.aging_90}</div>
        <div class="kpi"><b>Sem fotos</b>${rep.sem_foto}</div>
        <div class="kpi"><b>Sem preço</b>${rep.sem_preco}</div>
      </div>
      <h2>Por marca (disponíveis)</h2>
      <table><tr><th>Marca</th><th>Qtde</th><th>Valor</th></tr>${rows}</table>
    </body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  const KpiBox = ({ label, value, warn }) => (
    <div className="rounded-lg border border-border bg-popover px-3.5 py-2.5">
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground/60 font-bold">{label}</p>
      <p className={`font-display font-bold text-lg tabular-nums ${warn ? 'text-amber-400' : 'text-foreground'}`}>{value}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl uppercase flex items-center gap-2" data-testid="report-title"><FileBarChart className="w-5 h-5 text-primary" /> Relatório de Estoque</DialogTitle>
        </DialogHeader>
        {!rep ? <p className="text-muted-foreground text-sm py-8 text-center">Carregando...</p> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="report-kpis">
              <KpiBox label="Total de veículos" value={rep.total} />
              <KpiBox label="Disponíveis" value={rep.disponiveis} />
              <KpiBox label="Valor do estoque" value={formatCurrency(rep.valor_estoque)} />
              <KpiBox label="Aging médio" value={`${rep.aging_medio}d`} />
              {isAdmin && <KpiBox label="Custo total" value={formatCurrency(rep.custo_total || 0)} />}
              {isAdmin && <KpiBox label="Gastos total" value={formatCurrency(rep.gastos_total || 0)} />}
              {isAdmin && <KpiBox label="Lucro estimado" value={formatCurrency(rep.lucro_estimado || 0)} />}
              <KpiBox label="30–59 dias" value={rep.aging_30} warn={rep.aging_30 > 0} />
              <KpiBox label="60–89 dias" value={rep.aging_60} warn={rep.aging_60 > 0} />
              <KpiBox label="90+ dias" value={rep.aging_90} warn={rep.aging_90 > 0} />
              <KpiBox label="Sem fotos" value={rep.sem_foto} warn={rep.sem_foto > 0} />
              <KpiBox label="Sem preço" value={rep.sem_preco} warn={rep.sem_preco > 0} />
            </div>
            <div className="rounded-lg border border-border/70 p-4">
              <p className="text-[10px] uppercase font-bold text-muted-foreground/70 mb-2">Por marca (disponíveis)</p>
              <div className="max-h-[200px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-muted-foreground text-[11px] border-b border-border/70"><th className="pb-2">MARCA</th><th className="pb-2">QTDE</th><th className="pb-2 text-right">VALOR</th></tr></thead>
                  <tbody>{rep.by_marca.map((m) => (
                    <tr key={m.marca} className="border-b border-border/40"><td className="py-1.5 font-medium">{m.marca}</td><td className="py-1.5">{m.count}</td><td className="py-1.5 text-right tabular-nums">{formatCurrency(m.valor)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button data-testid="report-csv" onClick={dlCsv} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-muted/50 border border-border hover:bg-muted/80 transition-colors"><Download className="w-4 h-4" /> Exportar CSV</button>
              <button data-testid="report-print" onClick={printReport} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors"><Printer className="w-4 h-4" /> Imprimir / PDF</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Página ---------- */
const emptyAdv = { marca: '', modelo: '', cor: '', anoMin: '', anoMax: '', precoMin: '', precoMax: '', kmMin: '', kmMax: '', aging: '', semFoto: false, semPreco: false, margemBaixa: false, comFoto: false, agingMax: '' };

const PRICE_BANDS = [['1', '10000', 'Até $10k'], ['10000', '20000', '$10k–20k'], ['20000', '30000', '$20k–30k'], ['30000', '40000', '$30k–40k'], ['40000', '', '$40k+']];
const MILE_BANDS = [['', '30000', 'Até 30k mi'], ['30000', '60000', '30–60k mi'], ['60000', '100000', '60–100k mi'], ['100000', '', '100k+ mi']];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Estoque = () => {
  const { user } = useAuth();
  const isAdmin = ['Administrator', 'Owner'].includes(user?.role) || (user?.role || '').endsWith('Manager');
  const canEditInv = ['Administrator', 'Owner', 'General Manager', 'Sales Manager'].includes(user?.role) || user?.permissions?.includes('edit_inventory');
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('Todos');
  const [invView, setInvView] = useState(() => localStorage.getItem('sm-inv-view')
    || (typeof window !== 'undefined' && window.innerWidth < 768 ? 'grid' : 'list'));
  useEffect(() => { localStorage.setItem('sm-inv-view', invView); }, [invView]);
  const [query, setQuery] = useState('');
  const [adv, setAdv] = useState(emptyAdv);
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [advOpen, setAdvOpen] = useState(false);
  const [dOpen, setDOpen] = useState(false);
  const [vinDup, setVinDup] = useState(null);
  const [vinChecking, setVinChecking] = useState(false);
  const [repOpen, setRepOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [manageId, setManageId] = useState(null);
  const [viewerV, setViewerV] = useState(null);
  const [deleteV, setDeleteV] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);
  const [form, setForm] = useState({ vin: '', marca: '', modelo: '', trim: '', ano: '', km: '', cor: '', preco: '', custo: '', title_absent: false, floor_plan: '' });
  const [newPhotos, setNewPhotos] = useState([]);
  const [upProgress, setUpProgress] = useState(null);
  const newPhotoRef = useRef(null);
  const [marginCfg, setMarginCfg] = useState(null);
  const [marginOpen, setMarginOpen] = useState(false);
  const [marginDraft, setMarginDraft] = useState({ enabled: true, min_amount: '', min_pct: '', notify: true });
  const [savingMargin, setSavingMargin] = useState(false);

  const manageV = useMemo(() => list.find((v) => v.id === manageId) || null, [list, manageId]);
  useDeepLink('v', setManageId);

  const load = async () => {
    try { const res = await api.get('/vehicles'); setList(res.data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const loadMargin = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get('/settings/margin');
      setMarginCfg(data);
      setMarginDraft({ enabled: data.enabled, min_amount: String(data.min_amount ?? ''), min_pct: String(data.min_pct ?? ''), notify: data.notify });
    } catch (e) {}
  }, [isAdmin]);
  useEffect(() => { loadMargin(); }, [loadMargin]);

  const saveMargin = async () => {
    setSavingMargin(true);
    try {
      await api.put('/settings/margin', {
        enabled: marginDraft.enabled, notify: marginDraft.notify,
        min_amount: Number(marginDraft.min_amount) || 0, min_pct: Number(marginDraft.min_pct) || 0,
      });
      setMarginOpen(false);
      await loadMargin();
      await load();
      toast({ title: 'Margem mínima atualizada' });
    } catch (e) { toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || '' }); }
    finally { setSavingMargin(false); }
  };

  useEffect(() => {
    window.CarGurus = window.CarGurus || {};
    window.CarGurus.DealRatingBadge = window.CarGurus.DealRatingBadge || {};
    window.CarGurus.DealRatingBadge.options = { minRating: 'FAIR_PRICE', style: 'STYLE1' };
    if (!document.getElementById('cg-badge-sdk')) {
      const s = document.createElement('script');
      s.id = 'cg-badge-sdk';
      s.src = 'https://static.cargurus.com/js/api/en_US/1.0/dealratingbadge.js';
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  const decodeVin = async () => {
    const vin = (form.vin || '').trim();
    if (vin.length < 11) { toast({ title: 'VIN inválido', description: 'Informe um VIN de 17 caracteres.' }); return; }
    setDecoding(true);
    try {
      const { data } = await api.get(`/vin/decode/${vin}`);
      setForm((f) => ({ ...f, marca: data.marca || f.marca, modelo: data.modelo || f.modelo, trim: data.trim || f.trim, ano: data.ano || f.ano }));
      toast({ title: 'VIN decodificado', description: `${data.ano} ${data.marca} ${data.modelo} ${data.trim}`.trim() });
    } catch (e) {
      toast({ title: 'Falha ao decodificar', description: 'Verifique o VIN e tente novamente.' });
    } finally { setDecoding(false); }
  };

  const scope = useMemo(() => (filter === 'Todos' ? list : list.filter((v) => v.status === filter)), [list, filter]);
  const alerts = useMemo(() => ({
    a30: scope.filter((v) => v.aging_days >= 30 && v.aging_days < 60).length,
    a60: scope.filter((v) => v.aging_days >= 60 && v.aging_days < 90).length,
    a90: scope.filter((v) => v.aging_days >= 90).length,
    semFoto: scope.filter((v) => !v.photos?.length && !v.image).length,
    semPreco: scope.filter((v) => !v.preco).length,
    margem: scope.filter((v) => v.margin_low).length,
  }), [scope]);

  const marcaCounts = useMemo(() => {
    const m = new Map();
    scope.forEach((v) => v.marca && m.set(v.marca, (m.get(v.marca) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [scope]);
  const modelos = useMemo(() => [...new Set(scope.filter((v) => !adv.marca || v.marca === adv.marca).map((v) => v.modelo).filter(Boolean))].sort(), [scope, adv.marca]);
  const cores = useMemo(() => [...new Set(scope.map((v) => v.cor).filter((c) => c && c !== '-'))].sort(), [scope]);

  const SUGGESTIONS = useMemo(() => [
    ['pronto', 'Prontos para anunciar', { comFoto: true, precoMin: '1' }, (v) => (v.photos?.length || v.image) && v.preco > 0],
    ['giro', 'Entraram há menos de 30 dias', { agingMax: '29' }, (v) => v.aging_days < 30],
    ['aging-sem-preco', 'Aging 60+ sem preço', { aging: '60', semPreco: true }, (v) => v.aging_days >= 60 && !v.preco],
    ['baratos', 'Até $10k', { precoMin: '1', precoMax: '10000' }, (v) => v.preco > 0 && v.preco <= 10000],
    ['premium', 'Acima de $30k', { precoMin: '30000' }, (v) => v.preco >= 30000],
    ['baixa-milhagem', 'Baixa milhagem (<40k mi)', { kmMax: '40000' }, (v) => v.km > 0 && v.km < 40000],
    ['alta-milhagem', 'Alta milhagem (100k+)', { kmMin: '100000' }, (v) => v.km >= 100000],
    ['novos', 'Ano 2022+', { anoMin: '2022' }, (v) => Number(v.ano) >= 2022],
  ], []);

  const advActive = Object.entries(adv).some(([k, val]) => val !== emptyAdv[k]);

  const filtered = useMemo(() => list.filter((v) => {
    if (filter !== 'Todos' && v.status !== filter) return false;
    const q = query.toLowerCase();
    if (q && !`${v.marca} ${v.modelo} ${v.vin || ''} ${v.placa || ''}`.toLowerCase().includes(q)) return false;
    if (adv.marca && v.marca !== adv.marca) return false;
    if (adv.modelo && v.modelo !== adv.modelo) return false;
    if (adv.cor && v.cor !== adv.cor) return false;
    if (adv.anoMin && Number(v.ano) < Number(adv.anoMin)) return false;
    if (adv.anoMax && Number(v.ano) > Number(adv.anoMax)) return false;
    if (adv.precoMin && v.preco < Number(adv.precoMin)) return false;
    if (adv.precoMax && v.preco > Number(adv.precoMax)) return false;
    if (adv.kmMin && Number(v.km || 0) < Number(adv.kmMin)) return false;
    if (adv.kmMax && Number(v.km || 0) > Number(adv.kmMax)) return false;
    if (adv.aging && v.aging_days < Number(adv.aging)) return false;
    if (adv.agingMax && v.aging_days > Number(adv.agingMax)) return false;
    if (adv.semFoto && (v.photos?.length || v.image)) return false;
    if (adv.comFoto && !(v.photos?.length || v.image)) return false;
    if (adv.semPreco && v.preco > 0) return false;
    if (adv.margemBaixa && !v.margin_low) return false;
    return true;
  }), [list, filter, query, adv]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const num = (v) => Number(v || 0);
    return [...filtered].sort((a, b) => {
      if (sort.key === 'marca') {
        const s = `${a.marca || ''} ${a.modelo || ''}`.localeCompare(`${b.marca || ''} ${b.modelo || ''}`, 'pt-BR');
        return s * dir;
      }
      return (num(a[sort.key]) - num(b[sort.key])) * dir;
    });
  }, [filtered, sort]);

  const toggleSort = (key) => setSort((s) => (s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : { key: '', dir: 'asc' }));

  useEffect(() => {
    const t = setTimeout(() => { try { window.CarGurus?.DealRatingBadge?.run?.(); } catch (e) {} }, 500);
    return () => clearTimeout(t);
  }, [filtered]);

  const pickNewPhotos = (files) => {
    const merged = [...newPhotos, ...Array.from(files)].slice(0, 20);
    if (newPhotos.length + files.length > 20) toast({ title: 'Máximo de 20 fotos' });
    setNewPhotos(merged);
  };

  const checkVin = async (vin) => {
    const v = (vin || '').trim().toUpperCase();
    if (v.length < 11) { setVinDup(null); return null; }
    setVinChecking(true);
    try {
      const { data } = await api.get(`/vehicles/vin-check/${v}`);
      setVinDup(data);
      return data;
    } catch { setVinDup(null); return null; }
    finally { setVinChecking(false); }
  };

  const handleAdd = async () => {
    if (saving) return;
    if (!form.marca || !form.modelo) { toast({ title: 'Preencha marca e modelo', description: 'Campos obrigatórios.' }); return; }
    setSaving(true);
    try {
      const chk = await checkVin(form.vin);
      if (chk?.duplicate) { toast({ title: 'VIN duplicado', description: chk.message }); return; }
      const payload = {
        vin: form.vin, marca: form.marca, modelo: form.modelo, trim: form.trim,
        ano: Number(form.ano) || 2024, km: Number(form.km) || 0,
        cor: form.cor || '-', preco: Number(form.preco) || 0, image: '',
        title_absent: !!form.title_absent, floor_plan: form.floor_plan || '',
      };
      if (isAdmin && form.custo) payload.custo = Number(form.custo) || 0;
      const { data: created } = await api.post('/vehicles', payload);
      let ok = 0;
      if (newPhotos.length) {
        setUpProgress({ done: 0, total: newPhotos.length, ok: 0 });
        const res = await uploadPhotoBatch(api, `/vehicles/${created.id}/photos`, newPhotos, setUpProgress);
        ok = res.ok;
        setUpProgress(null);
        if (res.failed.length) toast({ title: `${res.failed.length} foto(s) não subiram`, description: res.failed.slice(0, 3).map((f) => `${f.name}: ${f.reason}`).join(' · ') });
      }
      setForm({ vin: '', marca: '', modelo: '', trim: '', ano: '', km: '', cor: '', preco: '', custo: '', title_absent: false, floor_plan: '' });
      setNewPhotos([]);
      setVinDup(null);
      setDOpen(false);
      await load();
      toast({ title: 'Veículo adicionado', description: `${form.marca} ${form.modelo} entrou no estoque${ok ? ` com ${ok} foto(s)` : ''}.` });
    } catch (e) {
      const det = e.response?.data?.detail;
      const msg = Array.isArray(det) ? det.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join(' · ') : det || (e.response ? `Erro ${e.response.status}` : `Sem resposta do servidor (${e.message}). Verifique a conexão e tente novamente.`);
      toast({ title: 'Erro ao salvar', description: msg });
    } finally { setSaving(false); }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const { data } = await api.post('/inventory/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      toast({ title: '✅ Inventário importado', description: `${data.imported} veículos importados · ${data.removed} antigos removidos${data.sem_preco ? ` · ${data.sem_preco} sem preço` : ''}` });
      setImportFile(null);
      await load();
    } catch (e) { toast({ title: 'Erro na importação', description: e.response?.data?.detail || 'Verifique o arquivo e tente novamente.' }); }
    finally { setImporting(false); }
  };

  const confirmDelete = async () => {
    if (!deleteV) return;
    setDeleting(true);
    try {
      await api.delete(`/vehicles/${deleteV.id}`);
      toast({ title: 'Veículo apagado', description: `${deleteV.ano} ${deleteV.marca} ${deleteV.modelo} removido do estoque.` });
      setDeleteV(null);
      await load();
    } catch (e) { toast({ title: 'Erro ao apagar', description: e.response?.data?.detail || '' }); }
    finally { setDeleting(false); }
  };

  const AlertChip = null;

  const SortTh = ({ k, label, cls = '' }) => (
    <th className={cls}>
      <button data-testid={`sort-th-${k}`} onClick={() => toggleSort(k)} className={`inline-flex items-center gap-1 uppercase tracking-[0.1em] ${sort.key === k ? 'text-primary' : 'hover:text-foreground'}`}>
        {label}
        {sort.key === k ? (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </button>
    </th>
  );

  const InventoryTable = () => (
    <div className="ent-panel overflow-x-auto" data-testid="inventory-table">
      <table className="ent-table">
        <thead>
          <tr>
            <th style={{ width: 56 }}></th>
            <SortTh k="marca" label="Veículo" />
            <th className="hidden xl:table-cell">VIN</th>
            <SortTh k="km" label="Miles" cls="text-right" />
            {isAdmin && <th className="text-right">Custo</th>}
            <SortTh k="preco" label="Preço" cls="text-right" />
            {isAdmin && <th className="text-right">Margem</th>}
            <SortTh k="aging_days" label="Dias" cls="text-right" />
            <th>Status</th>
            <th className="text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((v) => {
            const cover = coverUrl(v);
            const margem = (v.preco || 0) - (v.custo || 0) - (v.expenses_total || 0) + (v.backend_net || 0);
            const st = v.status === 'Disponível' ? 't-ok' : v.status === 'Reservado' ? 't-warn' : 'text-muted-foreground';
            return (
              <tr key={v.id} data-testid={`vehicle-row-${v.placa}`}>
                <td>
                  <button onClick={() => cover && setViewerV(v)} className="block w-12 h-12 overflow-hidden border border-border bg-black rounded-[2px]">
                    {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center"><ImageOff className="w-4 h-4 text-muted-foreground/50" /></span>}
                  </button>
                </td>
                <td>
                  <p className="font-medium">{v.ano} {v.marca} {v.modelo}{v.trim ? <span className="text-muted-foreground font-normal"> {v.trim.split(',')[0]}</span> : null}</p>
                  <p className="text-[10px] t-label mono">#{v.placa} · {v.cor || '—'} · {v.leads_count || 0} leads</p>
                </td>
                <td className="hidden xl:table-cell">
                  <span className="inline-flex items-center gap-1.5 mono text-[11px] text-muted-foreground">
                    {v.vin || '—'}
                    {v.vin && <button data-testid={`copy-vin-${v.placa}`} title="Copiar VIN" onClick={() => { navigator.clipboard?.writeText(v.vin); toast({ title: 'VIN copiado' }); }} className="text-muted-foreground/60 hover:text-primary"><Copy className="w-3 h-3" /></button>}
                  </span>
                </td>
                <td className="text-right num text-muted-foreground">{formatKm(v.km)}</td>
                {isAdmin && <td className="text-right num text-muted-foreground">{formatCurrency(v.custo || 0)}</td>}
                <td className="text-right num font-semibold text-foreground">{v.preco > 0 ? formatCurrency(v.preco) : <span className="t-warn">Sem preço</span>}</td>
                {isAdmin && (
                  <td className={`text-right num font-semibold ${v.margin_low ? 't-bad' : margem >= 0 ? 't-ok' : 't-bad'}`} data-testid={`vehicle-margin-${v.placa}`}>
                    {formatCurrency(margem)}
                    {v.margin_low && <span className="ml-1.5 ent-tag t-bad" title={`Abaixo da margem mínima${marginCfg ? ` (${formatCurrency(marginCfg.min_amount)})` : ''}`} data-testid={`margin-low-${v.placa}`}>BAIXA</span>}
                  </td>
                )}
                <td className={`text-right num ${v.aging_days >= 90 ? 't-bad' : v.aging_days >= 60 ? 't-warn' : 'text-muted-foreground'}`}>{v.aging_days}</td>
                <td><span className={`ent-status ${st}`}><span className="ent-dot" />{v.status}</span></td>
                <td>
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`vehicle-share-${v.placa}`} title="Compartilhar" onClick={() => shareVehicle(v)} className="ent-btn ent-btn-icon border-0"><Share2 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`vehicle-manage-${v.placa}`} title="Gerenciar" onClick={() => setManageId(v.id)} className="ent-btn ent-btn-icon border-0"><ClipboardEdit className="w-3.5 h-3.5" /></button>
                    <a href={v.vdp_url || `https://www.speedmotorsma.com/inventory/?s=${encodeURIComponent(v.vin || v.placa || '')}`} target="_blank" rel="noreferrer" data-testid={`vehicle-details-${v.placa}`} title="Ver no site" className="ent-btn ent-btn-icon border-0"><ExternalLink className="w-3.5 h-3.5" /></a>
                    {canEditInv && <button data-testid={`vehicle-delete-${v.placa}`} title="Apagar" onClick={() => setDeleteV(v)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Inventory intelligence ribbon */}
      <div className="ent-ribbon" data-testid="inventory-alerts">
        <div>
          <p className="ent-label">Unidades · {filter}{advActive ? ' (filtrado)' : ''}</p>
          <p className="ent-value mt-1" data-testid="inv-total-units">{sorted.length}</p>
          <p className="text-[10.5px] text-muted-foreground/60 mt-0.5">de {list.length} no total</p>
        </div>
        <div>
          <p className="ent-label">Valor total</p>
          <p className="ent-value mt-1" data-testid="inv-total-value">{formatCurrency(sorted.reduce((s, v) => s + (v.preco || 0), 0))}</p>
          <p className="text-[10.5px] text-muted-foreground/60 mt-0.5">média {formatCurrency(sorted.length ? sorted.reduce((s, v) => s + (v.preco || 0), 0) / sorted.length : 0)}</p>
        </div>
        <div>
          <p className="ent-label">Miles média</p>
          <p className="ent-value mt-1" data-testid="inv-avg-miles">{formatKm(sorted.length ? Math.round(sorted.reduce((s, v) => s + (v.km || 0), 0) / sorted.length) : 0)}</p>
          <p className="text-[10.5px] text-muted-foreground/60 mt-0.5">{sorted.length ? Math.round(sorted.reduce((s, v) => s + (v.aging_days || 0), 0) / sorted.length) : 0} dias em média</p>
        </div>
        <button data-testid="alert-30" onClick={() => setAdv((a) => ({ ...emptyAdv, aging: a.aging === '30' ? '' : '30' }))} className={`text-left transition-colors hover:bg-primary/5 ${adv.aging === '30' ? 'bg-primary/[0.07]' : ''}`}>
          <p className="ent-label">Retenção 30+ dias</p><p className={`ent-value mt-1 ${alerts.a30 ? 't-warn' : ''}`}>{alerts.a30}</p>
        </button>
        <button data-testid="alert-60" onClick={() => setAdv((a) => ({ ...emptyAdv, aging: a.aging === '60' ? '' : '60' }))} className={`text-left transition-colors hover:bg-primary/5 ${adv.aging === '60' ? 'bg-primary/[0.07]' : ''}`}>
          <p className="ent-label">60+ dias</p><p className={`ent-value mt-1 ${alerts.a60 ? 't-warn' : ''}`}>{alerts.a60}</p>
        </button>
        <button data-testid="alert-90" onClick={() => setAdv((a) => ({ ...emptyAdv, aging: a.aging === '90' ? '' : '90' }))} className={`text-left transition-colors hover:bg-primary/5 ${adv.aging === '90' ? 'bg-primary/[0.07]' : ''}`}>
          <p className="ent-label">90+ dias</p><p className={`ent-value mt-1 ${alerts.a90 ? 't-bad' : ''}`}>{alerts.a90}</p>
        </button>
        <button data-testid="alert-semfoto" onClick={() => setAdv((a) => ({ ...emptyAdv, semFoto: !a.semFoto }))} className={`text-left transition-colors hover:bg-primary/5 ${adv.semFoto ? 'bg-primary/[0.07]' : ''}`}>
          <p className="ent-label">Sem fotos</p><p className={`ent-value mt-1 ${alerts.semFoto ? 't-warn' : ''}`}>{alerts.semFoto}</p>
        </button>
        <button data-testid="alert-sempreco" onClick={() => setAdv((a) => ({ ...emptyAdv, semPreco: !a.semPreco }))} className={`text-left transition-colors hover:bg-primary/5 ${adv.semPreco ? 'bg-primary/[0.07]' : ''}`}>
          <p className="ent-label">Sem preço</p><p className={`ent-value mt-1 ${alerts.semPreco ? 't-warn' : ''}`}>{alerts.semPreco}</p>
        </button>
        {isAdmin && (
          <button data-testid="alert-margem" onClick={() => setAdv((a) => ({ ...emptyAdv, margemBaixa: !a.margemBaixa }))} className={`text-left transition-colors hover:bg-primary/5 ${adv.margemBaixa ? 'bg-primary/[0.07]' : ''}`}>
            <p className="ent-label">Margem baixa</p>
            <p className={`ent-value mt-1 ${alerts.margem ? 't-bad' : ''}`} data-testid="alert-margem-count">{alerts.margem}</p>
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mob-chiprow" data-testid="inv-smart-filters">
        <span className="ent-label mr-0.5">Filtros rápidos</span>
        {SUGGESTIONS.map(([key, label, patch, pred]) => {
          const n = scope.filter(pred).length;
          const on = Object.entries(patch).every(([k, val]) => adv[k] === val);
          if (!n && !on) return null;
          return (
            <button key={key} data-testid={`smart-${key}`} onClick={() => setAdv(on ? emptyAdv : { ...emptyAdv, ...patch })}
              className={`ent-btn ent-btn-sm ${on ? 'text-primary border-primary/60' : ''}`}>
              {label} <span className="ent-pill ml-1">{n}</span>
            </button>
          );
        })}
        {advActive && <button data-testid="smart-clear" onClick={() => setAdv(emptyAdv)} className="ent-btn ent-btn-sm text-muted-foreground"><X className="w-3 h-3" /> Limpar</button>}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 flex-wrap mob-chiprow">
          {statusFilters.map((f) => (
            <button key={f} data-testid={`status-filter-${f}`} onClick={() => setFilter(f)} className={`ent-btn ent-btn-sm ${filter === f ? 'ent-btn-primary' : ''}`}>{f}</button>
          ))}
          <button data-testid="adv-filters-btn" onClick={() => setAdvOpen(!advOpen)} className={`ent-btn ent-btn-sm ${advActive || advOpen ? 'text-primary border-primary/60' : ''}`}><SlidersHorizontal className="w-3.5 h-3.5" /> Filtros{advActive ? ' •' : ''}</button>
          <span className="ent-label ml-1.5 hidden md:inline">Ordenar</span>
          {[['preco', 'Preço'], ['km', 'Miles'], ['ano', 'Ano'], ['marca', 'Marca'], ['aging_days', 'Dias']].map(([k, label]) => {
            const on = sort.key === k;
            const Icon = !on ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
            return (
              <button key={k} data-testid={`sort-${k}`} onClick={() => toggleSort(k)}
                title={on ? (sort.dir === 'asc' ? 'Crescente — clique para inverter' : 'Decrescente — clique para limpar') : 'Ordenar'}
                className={`ent-btn ent-btn-sm ${on ? 'text-primary border-primary/60' : ''}`}>
                {label} <Icon className="w-3 h-3" />
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
          <div className="flex items-center gap-2 border border-border rounded-[3px] px-2.5 h-9 md:h-8 w-full md:w-44 shrink-0">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar veículo..." className="bg-transparent outline-none text-[12.5px] w-full placeholder:text-muted-foreground/60" />
          </div>
          <div className="flex border border-border rounded-[3px] overflow-hidden shrink-0">
            <button data-testid="inv-view-list" onClick={() => setInvView('list')} title="Lista" className={`w-10 h-9 md:w-8 md:h-8 shrink-0 flex items-center justify-center transition-colors ${invView === 'list' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}><List className="w-4 h-4" /></button>
            <button data-testid="inv-view-grid" onClick={() => setInvView('grid')} title="Fotos" className={`w-10 h-9 md:w-8 md:h-8 shrink-0 flex items-center justify-center border-l border-border transition-colors ${invView === 'grid' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}><LayoutGrid className="w-4 h-4" /></button>
          </div>
          <button data-testid="open-report-btn" onClick={() => setRepOpen(true)} className="ent-btn"><FileBarChart className="w-3.5 h-3.5" /> Relatório</button>
          {isAdmin && (
            <Dialog open={marginOpen} onOpenChange={setMarginOpen}>
              <DialogTrigger asChild>
                <button data-testid="margin-settings-btn" className={`ent-btn ${alerts.margem ? 'text-[hsl(var(--bad))] border-[hsl(var(--bad))]/50' : ''}`}><AlertTriangle className="w-3.5 h-3.5" /> Margem mín.</button>
              </DialogTrigger>
              <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
                <DialogHeader><DialogTitle className="font-display font-black text-lg uppercase">Alerta de margem mínima</DialogTitle></DialogHeader>
                <div className="space-y-4 py-1">
                  <p className="text-xs text-muted-foreground">O lucro efetivo considera preço + markups do back-end − custo − gastos − custos do back-end. Veículos abaixo do mínimo ficam marcados no estoque e geram notificação para Admin, Owner e gerentes.</p>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input data-testid="margin-enabled" type="checkbox" checked={marginDraft.enabled} onChange={(e) => setMarginDraft({ ...marginDraft, enabled: e.target.checked })} className="accent-blue-500" /> Alerta ativo
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="ent-label block mb-1.5">Lucro mínimo ($)</label><input data-testid="margin-min-amount" type="number" className={inputCls} value={marginDraft.min_amount} onChange={(e) => setMarginDraft({ ...marginDraft, min_amount: e.target.value })} placeholder="1500" /></div>
                    <div><label className="ent-label block mb-1.5">Margem mínima (%)</label><input data-testid="margin-min-pct" type="number" className={inputCls} value={marginDraft.min_pct} onChange={(e) => setMarginDraft({ ...marginDraft, min_pct: e.target.value })} placeholder="0" /></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Deixe em 0 o critério que não quiser usar. Se os dois forem preenchidos, o alerta dispara quando qualquer um for violado.</p>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input data-testid="margin-notify" type="checkbox" checked={marginDraft.notify} onChange={(e) => setMarginDraft({ ...marginDraft, notify: e.target.checked })} className="accent-blue-500" /> Notificar no sino (Admin, Owner e gerentes)
                  </label>
                </div>
                <DialogFooter>
                  <button onClick={() => setMarginOpen(false)} className="ent-btn">Cancelar</button>
                  <button data-testid="margin-save" onClick={saveMargin} disabled={savingMargin} className="ent-btn ent-btn-primary">{savingMargin ? 'Salvando...' : 'Salvar'}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isAdmin && (
            <>
              <input ref={importRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { if (e.target.files?.[0]) setImportFile(e.target.files[0]); e.target.value = ''; }} />
              <button data-testid="import-csv-btn" onClick={() => importRef.current?.click()} className="ent-btn"><Upload className="w-3.5 h-3.5" /> Importar CSV</button>
            </>
          )}
          <Dialog open={dOpen} onOpenChange={(o) => { setDOpen(o); if (!o) { setNewPhotos([]); setVinDup(null); } }}>
            <DialogTrigger asChild>
              <button data-testid="new-vehicle-btn" className="ent-btn ent-btn-primary"><Plus className="w-3.5 h-3.5" /> Novo veículo</button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-xl max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Novo veículo</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground mb-1.5 block">VIN</label>
                  <div className="flex gap-2">
                    <input data-testid="vehicle-vin-input" className={`${inputCls} font-mono uppercase ${vinDup?.duplicate ? 'border-[hsl(var(--bad))]' : ''}`} value={form.vin} maxLength={17}
                      onChange={(e) => { setForm({ ...form, vin: e.target.value.toUpperCase() }); setVinDup(null); }}
                      onBlur={() => checkVin(form.vin)} placeholder="Ex: 3KPF24AD1PE674771" />
                    <VinScanButton testid="vehicle-vin-scan" className="py-2.5" onResult={(r) => { const d = r.decoded || {}; setForm((f) => ({ ...f, vin: r.vin, marca: d.marca || f.marca, modelo: d.modelo || f.modelo, trim: d.trim || f.trim, ano: d.ano || f.ano })); setVinDup(null); checkVin(r.vin); }} />
                    <button data-testid="vin-decode-btn" onClick={decodeVin} disabled={decoding} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-muted/50 border border-border hover:bg-muted/80 transition-colors whitespace-nowrap disabled:opacity-60">{decoding ? 'Decodificando...' : 'Decodificar'}</button>
                  </div>
                  <div className="min-h-[20px] mt-1.5">
                    {vinChecking && <p className="text-[11px] t-label">Verificando VIN no estoque...</p>}
                    {vinDup?.duplicate && (
                      <p data-testid="vin-duplicate-warning" className="text-[12px] t-bad border-l-2 border-[hsl(var(--bad))] pl-2">
                        VIN já cadastrado: {vinDup.vehicle?.ano} {vinDup.vehicle?.marca} {vinDup.vehicle?.modelo} · #{vinDup.vehicle?.placa} · status <b>{vinDup.vehicle?.status}</b>.
                        Não é possível cadastrar em duplicidade.
                      </p>
                    )}
                    {vinDup && !vinDup.duplicate && !vinChecking && <p data-testid="vin-ok" className="text-[12px] t-ok">VIN livre — nenhum veículo com esse VIN no sistema.</p>}
                  </div>
                </div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Marca</label><input className={inputCls} value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} placeholder="Ex: Toyota" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Modelo</label><input className={inputCls} value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} placeholder="Ex: Corolla" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Ano</label><input className={inputCls} value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} placeholder="2024" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Mileage (mi)</label><input className={inputCls} value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} placeholder="0" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Trim</label><input data-testid="vehicle-trim-input" className={inputCls} value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })} placeholder="Ex: LX, Limited" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Cor</label><input className={inputCls} list="vehicle-colors-new" value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })} placeholder="Prata" /><datalist id="vehicle-colors-new">{VEHICLE_COLORS.map((c) => <option key={c} value={c} />)}</datalist></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Preço de venda ($)</label><input data-testid="new-vehicle-preco" className={inputCls} value={form.preco} onChange={(e) => setForm({ ...form, preco: e.target.value })} placeholder="15000" /></div>
                {isAdmin && <div><label className="text-xs text-muted-foreground mb-1.5 block">Custo ($)</label><input data-testid="new-vehicle-custo" className={inputCls} value={form.custo} onChange={(e) => setForm({ ...form, custo: e.target.value })} placeholder="13000" /></div>}
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Floor plan (opcional)</label><input data-testid="new-vehicle-floor-plan" className={inputCls} value={form.floor_plan} onChange={(e) => setForm({ ...form, floor_plan: e.target.value })} placeholder="Ex: NextGear, Westlake, Cash" /></div>
                <label className="flex items-center gap-2 text-sm cursor-pointer self-end pb-2.5">
                  <input data-testid="new-vehicle-title-absent" type="checkbox" checked={!!form.title_absent} onChange={(e) => setForm({ ...form, title_absent: e.target.checked })} className="accent-blue-500" />
                  Title absent (opcional)
                </label>
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground mb-1.5 block">Fotos (até 20 — a 1ª será a capa)</label>
                  <input ref={newPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { pickNewPhotos(e.target.files); e.target.value = ''; }} />
                  <button data-testid="new-vehicle-photos-btn" onClick={() => newPhotoRef.current?.click()} className="w-full flex items-center justify-center gap-2 border border-dashed border-border hover:border-primary/50 rounded-lg py-3.5 text-sm text-muted-foreground hover:text-foreground transition-colors"><Camera className="w-4 h-4" /> {newPhotos.length ? `${newPhotos.length}/20 selecionadas — adicionar mais` : 'Selecionar fotos'}</button>
                  {newPhotos.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mt-2.5">
                      {newPhotos.map((f, i) => (
                        <div key={i} className="relative rounded-md overflow-hidden border border-border aspect-square bg-black">
                          <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                          {i === 0 && <span className="absolute top-1 left-1 text-[8px] font-black uppercase bg-primary text-white px-1 py-0.5 rounded">Capa</span>}
                          <button onClick={() => setNewPhotos(newPhotos.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 rounded p-0.5 text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setDOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                <button data-testid="new-vehicle-save" onMouseDown={(e) => e.preventDefault()} onClick={handleAdd} disabled={saving || !!vinDup?.duplicate} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? (upProgress ? `Enviando fotos ${upProgress.done}/${upProgress.total}...` : 'Salvando...') : vinDup?.duplicate ? 'VIN duplicado' : 'Adicionar'}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {advOpen && (
        <div className="rounded-xl border border-border/70 bg-card p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 items-end" data-testid="adv-filters-panel">
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Marca</label>
            <select data-testid="filter-marca" className={inputCls} value={adv.marca} onChange={(e) => setAdv({ ...adv, marca: e.target.value, modelo: '' })}>
              <option value="" className="bg-popover">Todas ({scope.length})</option>
              {marcaCounts.map(([m, n]) => <option key={m} value={m} className="bg-popover">{`${m} (${n})`}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Modelo</label>
            <select data-testid="filter-modelo" className={inputCls} value={adv.modelo} onChange={(e) => setAdv({ ...adv, modelo: e.target.value })}>
              <option value="" className="bg-popover">Todos</option>
              {modelos.map((m) => <option key={m} value={m} className="bg-popover">{m}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Cor</label>
            <select data-testid="filter-cor" className={inputCls} value={adv.cor} onChange={(e) => setAdv({ ...adv, cor: e.target.value })}>
              <option value="" className="bg-popover">Todas</option>
              {cores.map((c) => <option key={c} value={c} className="bg-popover">{c}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Ano mín</label><input data-testid="filter-ano-min" type="number" className={inputCls} value={adv.anoMin} onChange={(e) => setAdv({ ...adv, anoMin: e.target.value })} placeholder="2015" /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Ano máx</label><input data-testid="filter-ano-max" type="number" className={inputCls} value={adv.anoMax} onChange={(e) => setAdv({ ...adv, anoMax: e.target.value })} placeholder="2026" /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Preço mín ($)</label><input data-testid="filter-preco-min" type="number" className={inputCls} value={adv.precoMin} onChange={(e) => setAdv({ ...adv, precoMin: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Preço máx ($)</label><input data-testid="filter-preco-max" type="number" className={inputCls} value={adv.precoMax} onChange={(e) => setAdv({ ...adv, precoMax: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Miles mín</label><input data-testid="filter-km-min" type="number" className={inputCls} value={adv.kmMin} onChange={(e) => setAdv({ ...adv, kmMin: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Miles máx</label><input data-testid="filter-km-max" type="number" className={inputCls} value={adv.kmMax} onChange={(e) => setAdv({ ...adv, kmMax: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Aging</label>
            <select data-testid="filter-aging" className={inputCls} value={adv.aging} onChange={(e) => setAdv({ ...adv, aging: e.target.value })}>
              <option value="" className="bg-popover">Todos</option>
              <option value="30" className="bg-popover">30+ dias</option>
              <option value="60" className="bg-popover">60+ dias</option>
              <option value="90" className="bg-popover">90+ dias</option>
            </select>
          </div>
          <div className="col-span-2 md:col-span-4 lg:col-span-6 flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-3">
            <span className="ent-label mr-1">Faixa de preço</span>
            {PRICE_BANDS.map(([min, max, label]) => {
              const on = adv.precoMin === min && adv.precoMax === max;
              return <button key={label} data-testid={`band-price-${slug(label)}`} onClick={() => setAdv({ ...adv, precoMin: on ? '' : min, precoMax: on ? '' : max })} className={`ent-btn ent-btn-sm ${on ? 'text-primary border-primary/60' : ''}`}>{label}</button>;
            })}
          </div>
          <div className="col-span-2 md:col-span-4 lg:col-span-6 flex flex-wrap items-center gap-1.5">
            <span className="ent-label mr-1">Faixa de miles</span>
            {MILE_BANDS.map(([min, max, label]) => {
              const on = adv.kmMin === min && adv.kmMax === max;
              return <button key={label} data-testid={`band-km-${slug(label)}`} onClick={() => setAdv({ ...adv, kmMin: on ? '' : min, kmMax: on ? '' : max })} className={`ent-btn ent-btn-sm ${on ? 'text-primary border-primary/60' : ''}`}>{label}</button>;
            })}
          </div>
          <div className="col-span-2 md:col-span-4 lg:col-span-6 flex items-center gap-4 border-t border-border/60 pt-3">
            <label className="flex items-center gap-2 text-sm text-foreground/75 cursor-pointer"><input type="checkbox" checked={adv.semFoto} onChange={(e) => setAdv({ ...adv, semFoto: e.target.checked, comFoto: false })} className="accent-blue-500" /> Sem fotos</label>
            <label className="flex items-center gap-2 text-sm text-foreground/75 cursor-pointer"><input type="checkbox" checked={adv.comFoto} onChange={(e) => setAdv({ ...adv, comFoto: e.target.checked, semFoto: false })} className="accent-blue-500" /> Com fotos</label>
            <label className="flex items-center gap-2 text-sm text-foreground/75 cursor-pointer"><input type="checkbox" checked={adv.semPreco} onChange={(e) => setAdv({ ...adv, semPreco: e.target.checked })} className="accent-blue-500" /> Sem preço</label>
            {isAdmin && <label className="flex items-center gap-2 text-sm text-foreground/75 cursor-pointer"><input type="checkbox" checked={adv.margemBaixa} onChange={(e) => setAdv({ ...adv, margemBaixa: e.target.checked })} className="accent-blue-500" /> Margem baixa</label>}
            <button data-testid="clear-filters" onClick={() => setAdv(emptyAdv)} className="ml-auto text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">Limpar filtros</button>
            <span className="text-xs text-muted-foreground">{sorted.length} veículo(s)</span>
          </div>
        </div>
      )}

      {invView === 'grid' ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 stagger">
        {sorted.map((v) => (
          <div key={v.id} className="group rounded-xl border border-border bg-card overflow-hidden glow-border card-hover">
            <div className={`relative h-44 overflow-hidden bg-black ${coverUrl(v) ? 'cursor-zoom-in' : ''}`} data-testid={`vehicle-cover-${v.placa}`} onClick={() => coverUrl(v) && setViewerV(v)}>
              {coverUrl(v) ? (
                <img src={coverUrl(v)} alt={v.modelo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/50 gap-1.5"><ImageOff className="w-8 h-8" /><span className="text-[11px] font-semibold">Sem fotos</span></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
              <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${statusStyle[v.status] || statusStyle['Vendido']}`}><span className="w-1.5 h-1.5 rounded-full bg-current neon-dot" />{v.status}</span>
              {v.destaque && <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-primary flex items-center justify-center"><Star className="w-3.5 h-3.5 fill-white text-foreground" /></span>}
              <span data-testid={`vehicle-aging-${v.placa}`} className={`absolute bottom-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full border backdrop-blur-sm ${agingCls(v.aging_days)}`}><Clock className="w-3 h-3" />{v.aging_days}d</span>
              {(v.photos?.length > 0) && <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-black/60 border border-border text-foreground/80 backdrop-blur-sm"><Camera className="w-3 h-3" />{v.photos.length}</span>}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{v.marca} · {v.ano}</p>
                  <h3 className="font-display font-bold text-lg leading-tight">{v.modelo}{v.trim ? <span className="text-muted-foreground font-semibold text-sm"> {v.trim.split(',')[0]}</span> : null}</h3>
                </div>
                <span className="text-[11px] text-muted-foreground border border-border rounded px-1.5 py-0.5">{v.placa}</span>
              </div>
              {v.vin && <p data-testid={`vehicle-vin-${v.placa}`} className="mt-1.5 text-[11px] font-mono text-muted-foreground/70 tracking-wide">VIN: {v.vin}</p>}
              {(v.title_absent || v.floor_plan) && (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {v.title_absent && <span className="ent-tag t-warn" data-testid={`vehicle-title-absent-${v.placa}`}>Title absent</span>}
                  {v.floor_plan && <span className="ent-tag" data-testid={`vehicle-floor-plan-${v.placa}`}>FP: {v.floor_plan}</span>}
                </div>
              )}
              <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" />{formatKm(v.km)}</span>
                {v.combustivel && <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5" />{v.combustivel}</span>}
                {v.cambio && <span className="flex items-center gap-1.5"><Cog className="w-3.5 h-3.5" />{v.cambio}</span>}
                <span className={`flex items-center gap-1.5 ${v.leads_count > 0 ? 'text-primary font-semibold' : ''}`} data-testid={`vehicle-leads-${v.placa}`}><Users className="w-3.5 h-3.5" />{v.leads_count || 0} leads</span>
              </div>
              {isAdmin && (
                <div className="grid grid-cols-3 gap-2 mt-4 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5" data-testid={`vehicle-fin-${v.placa}`}>
                  <div><p className="text-[9px] uppercase text-muted-foreground/60 font-bold tracking-wider">Custo</p><p className="text-[12px] font-semibold tabular-nums">{formatCurrency(v.custo || 0)}</p></div>
                  <div><p className="text-[9px] uppercase text-muted-foreground/60 font-bold tracking-wider">MMR</p><p className="text-[12px] font-semibold tabular-nums">{v.mmr ? formatCurrency(v.mmr) : '—'}</p></div>
                  <div><p className="text-[9px] uppercase text-muted-foreground/60 font-bold tracking-wider">Gastos</p><p className="text-[12px] font-semibold tabular-nums text-orange-400" data-testid={`vehicle-gastos-${v.placa}`}>{formatCurrency(v.expenses_total || 0)}</p></div>
                  <div className="col-span-3"><p className="text-[9px] uppercase text-muted-foreground/60 font-bold tracking-wider">Lucro efetivo</p><p className={`text-[12px] font-bold tabular-nums ${v.margin_low ? 'text-red-400' : (v.profit || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`vehicle-profit-${v.placa}`}>{formatCurrency(v.profit || 0)}{v.margin_low && <span className="ml-1.5 ent-tag t-bad" data-testid={`margin-low-card-${v.placa}`}>MARGEM BAIXA</span>}</p></div>
                </div>
              )}
              <div className="flex items-end justify-between mt-4 pt-4 border-t border-border/70">
                <div>
                  <p className="text-[11px] text-muted-foreground">Preço</p>
                  {v.preco > 0 ? (
                    <p className="font-display font-black text-xl text-primary">{formatCurrency(v.preco)}</p>
                  ) : (
                    <p className="font-display font-black text-base text-fuchsia-400">Sem preço</p>
                  )}
                  {v.vin && v.preco > 0 && <span data-cg-vin={v.vin} data-cg-price={v.preco} data-cg-badge-style="STYLE1" data-cg-height="32" data-testid={`cg-badge-${v.placa}`} className="block mt-2" />}
                </div>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`vehicle-share-${v.placa}`} onClick={() => shareVehicle(v)} title="Compartilhar" className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-tech/60 transition-colors"><Share2 className="w-4 h-4" /></button>
                  <button data-testid={`vehicle-manage-${v.placa}`} onClick={() => setManageId(v.id)} title="Gerenciar (fotos, valores, anotações)" className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-primary/60 transition-colors"><ClipboardEdit className="w-4 h-4" /></button>
                  <a href={v.vdp_url || `https://www.speedmotorsma.com/inventory/?s=${encodeURIComponent(v.vin || v.placa || '')}`} target="_blank" rel="noreferrer" data-testid={`vehicle-details-${v.placa}`} title="Ver no site" className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-primary/60 transition-colors"><ExternalLink className="w-4 h-4" /></a>
                  {canEditInv && <button data-testid={`vehicle-delete-${v.placa}`} onClick={() => setDeleteV(v)} title="Apagar veículo" className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-red-400/60 hover:text-red-400 hover:border-red-400/60 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : <InventoryTable />}
      {sorted.length === 0 && <div className="text-center py-20 text-muted-foreground">Nenhum veículo encontrado.</div>}

      <VehicleManageDialog vehicle={manageV} onClose={() => setManageId(null)} onSaved={load} isAdmin={isAdmin} />
      <ReportDialog open={repOpen} onClose={() => setRepOpen(false)} isAdmin={isAdmin} />
      <PhotoLightbox vehicle={viewerV} onClose={() => setViewerV(null)} />

      <Dialog open={!!importFile} onOpenChange={(o) => !o && !importing && setImportFile(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Importar inventário?</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/80">Arquivo: <b>{importFile?.name}</b></p>
          <p className="text-xs text-amber-400 font-medium">⚠ Isso vai APAGAR todos os veículos atuais (incluindo fotos e anotações) e substituir pelo conteúdo do CSV. A decodificação dos VINs pode levar até 1 minuto.</p>
          <DialogFooter>
            <button data-testid="import-cancel" onClick={() => setImportFile(null)} disabled={importing} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors disabled:opacity-50">Cancelar</button>
            <button data-testid="import-confirm" onClick={confirmImport} disabled={importing} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{importing ? 'Importando...' : 'Substituir estoque'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteV} onOpenChange={(o) => !o && setDeleteV(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase text-red-400">Apagar veículo?</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/80">Tem certeza que deseja apagar <b>{deleteV?.ano} {deleteV?.marca} {deleteV?.modelo}</b> <span className="text-muted-foreground">#{deleteV?.placa}</span>?</p>
          <p className="text-xs text-muted-foreground">Fotos, anotações e histórico deste veículo também serão removidos. Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <button data-testid="delete-cancel" onClick={() => setDeleteV(null)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="delete-confirm" onClick={confirmDelete} disabled={deleting} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-60">{deleting ? 'Apagando...' : 'Apagar veículo'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Estoque;
